// The six cards' numbers on the Blunders or Misses screen: for every group
// of one kind of mistake (`mistakeKeys` of `@chess/api-mistakes`: motif
// (family, and family/variation), severity, standing, clock, material,
// week), the games with at least one such mistake, the mistakes, and their
// cost in pawns in the decisive zone (`costOf`). A game counts in a group
// exactly when the library's `mistake` filter keeps it for that key: both
// read `mistakeKeys` with the period's end as `now` and the same motif
// classifier (the `mistakes.motifs` service, memoised per fact by the
// plugin's setup, so a mistake is classified once however often the store
// publishes), so a bar's number is the library's count. Also the scanned
// games of each week (the week's rate) and the games whose mistakes had no
// clock (the clock card's note). One aggregator per kind.
import { aggregatorId, type Accumulator, type Aggregator, type AggregatorId, type GameFacts, type MoveKind, type PeriodContext } from '@chess/api-model';
import { costOf, MISTAKE_WEEKS, mistakeKeys, weekOf, type MistakeKey, type MistakeWeek, type MotifOf } from '@chess/api-mistakes';

/** One group's numbers. */
export interface GroupStat {
  /** Games with at least one mistake in the group: the library's count for its key. */
  games: number;
  /** Mistakes in the group. */
  mistakes: number;
  /** Their cost in pawns, in the decisive zone, added up. */
  cost: number;
}

export interface WeekStat {
  week: MistakeWeek;
  /** Mistakes of the kind in the week's scanned games. */
  mistakes: number;
  /** Scanned games played that week. */
  scanned: number;
}

export interface MistakeGroups {
  kind: MoveKind;
  /** Scanned games of the period. */
  scanned: number;
  /** Scanned games with at least one mistake of the kind. */
  games: number;
  /** Mistakes of the kind. */
  mistakes: number;
  /** Each group that has a mistake, by its key. */
  groups: Partial<Record<MistakeKey, GroupStat>>;
  /** The four weeks, oldest first (`MISTAKE_WEEKS`). */
  weeks: WeekStat[];
  /** Games with a mistake of the kind none of which has the clock before it: left out of the clock card. */
  noClock: number;
}

/** A group's numbers, zero when it has none. */
export const groupOf = (r: MistakeGroups, key: MistakeKey): GroupStat => r.groups[key] ?? { games: 0, mistakes: 0, cost: 0 };

class GroupsAccumulator implements Accumulator<MistakeGroups> {
  private readonly kind: MoveKind;
  private readonly now: number;
  private readonly motifOf: MotifOf;
  private scanned = 0;
  private games = 0;
  private mistakes = 0;
  private noClock = 0;
  private readonly groups: Partial<Record<MistakeKey, GroupStat>> = {};
  private readonly weeks: WeekStat[] = MISTAKE_WEEKS.map((week) => ({ week, mistakes: 0, scanned: 0 }));

  constructor(kind: MoveKind, now: number, motifOf: MotifOf) {
    this.kind = kind;
    this.now = now;
    this.motifOf = motifOf;
  }

  add(game: GameFacts): void {
    if (!game.scanned) return;
    this.scanned += 1;
    const week = weekOf(game.playedAt, this.now);
    const weekStat = week === undefined ? undefined : this.weeks[week];
    if (weekStat) weekStat.scanned += 1;
    const keys = new Set<MistakeKey>();
    let any = false;
    let clocked = false;
    for (const m of game.mistakes ?? []) {
      if (m.kind !== this.kind) continue;
      any = true;
      if (m.clock !== undefined) clocked = true;
      this.mistakes += 1;
      if (weekStat) weekStat.mistakes += 1;
      const cost = costOf(m);
      for (const key of mistakeKeys(this.kind, m, game, this.now, this.motifOf)) {
        const stat = (this.groups[key] ??= { games: 0, mistakes: 0, cost: 0 });
        stat.mistakes += 1;
        stat.cost += cost;
        keys.add(key);
      }
    }
    if (!any) return;
    this.games += 1;
    if (!clocked) this.noClock += 1;
    for (const key of keys) this.groups[key]!.games += 1;
  }

  result(): MistakeGroups {
    return { kind: this.kind, scanned: this.scanned, games: this.games, mistakes: this.mistakes, groups: this.groups, weeks: this.weeks, noClock: this.noClock };
  }
}

export class MistakeGroupsAggregator implements Aggregator<MistakeGroups> {
  readonly id: AggregatorId;
  readonly kind: MoveKind;
  private readonly motifOf: MotifOf;

  /** `motifOf`: the motif classifier, memoised (`memoisedMotifs` over the `mistakes.motifs` service). */
  constructor(kind: MoveKind, motifOf: MotifOf) {
    this.kind = kind;
    this.motifOf = motifOf;
    this.id = aggregatorId(`mistake-groups-${kind}`);
  }

  start(context: PeriodContext): Accumulator<MistakeGroups> {
    return new GroupsAccumulator(this.kind, context.to, this.motifOf);
  }
}
